import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { GET, POST, POST_IMAGE, PUT } from '../service/base-api-service';
import to from '../utils/to';
import { DEFAULT_PAGE_SIZE } from '../utils/constant';
import { getIsAdminApi, getUser } from './auth.store';
import get from 'lodash.get';
import { ROLE } from '../utils/features';

const initialState = {
  companyList: [],
  minimalCompanyList: [],
  minimalCompanyListLoading: false,
  tableInfo: {
    totalPages: 0,
    currentPage: -1,
    sortColumn: 'id',
    isAscending: true,
    searchKey: '',
  },
  isLoading: false,
  addNewCompanyLoading: false,
  isLoadingError: false,
  selectedCompany: {},
};

const normalizeRoles = (user) => {
  const scope = get(user, 'scope');
  const role = get(user, 'role');
  const roles = get(user, 'roles', []);
  const authorities = get(user, 'authorities', []);
  const authorityNames = Array.isArray(authorities)
    ? authorities.map((a) => (typeof a === 'string' ? a : a?.authority)).filter(Boolean)
    : [];

  const splitScope =
    typeof scope === 'string' ? scope.split(',').map((s) => s.trim()).filter(Boolean) : [];

  return Array.from(
    new Set([
      ...splitScope,
      ...(role ? [role] : []),
      ...(Array.isArray(roles) ? roles : []),
      ...authorityNames,
    ].filter(Boolean)),
  );
};

const hasMinearcAuthority = (user) => {
  const roles = normalizeRoles(user);
  return roles.some((r) =>
    [
      'ROLE_TYPE_MINEARC_ROLE',
      'ROLE_MINE_ARC_USER',
      'ROLE_MINE_ARC_SERVICE_TECHNICIAN',
      'ROLE_MINE_ARC_SERVICE_COORDINATOR',
      ROLE.MINEARC,
    ].includes(r),
  );
};

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  return payload ? [payload] : [];
};

const getTenantIdentifier = (item) =>
  get(item, 'identifier') ||
  get(item, 'tenant.identifier') ||
  get(item, 'tenant.id') ||
  get(item, 'tenantIdentifier') ||
  get(item, 'tenant_identifier') ||
  get(item, 'company.identifier') ||
  get(item, 'company.id') ||
  get(item, 'id') ||
  (typeof item === 'string' ? item : '');

const getTenantDisplayName = (item) =>
  get(item, 'name') ||
  get(item, 'tenant.name') ||
  get(item, 'tenantName') ||
  get(item, 'tenant_name') ||
  get(item, 'company.name') ||
  get(item, 'companyName') ||
  get(item, 'displayName') ||
  get(item, 'label') ||
  '';

const buildTenantOptionsFromSites = (sites) => {
  const map = new Map();

  (sites || []).forEach((site) => {
    const identifier = getTenantIdentifier(site);
    if (!identifier) return;
    const name = getTenantDisplayName(site);
    if (!map.has(identifier)) {
      map.set(identifier, {
        identifier,
        name: name || identifier,
      });
    }
  });

  return Array.from(map.values());
};

const hasReadableTenantName = (list) =>
  (list || []).some((item) => {
    const identifier = getTenantIdentifier(item);
    const name = getTenantDisplayName(item);
    return Boolean(name && identifier && String(name) !== String(identifier));
  });

export const getPaginatedCompanyList = createAsyncThunk(
  'companyList/get',
  async ({ page, sortColumn = 'id', isAscending = false, searchKey }, { dispatch }) => {
    dispatch(setTableInfo({ currentPage: page, sortColumn, isAscending, searchKey }));
    return GET('v1/admin/tenant', {
      params: { page, sortColumn, isAscending, size: DEFAULT_PAGE_SIZE, searchKey },
    });
  },
);

export const fetchMinimalCompanyList = createAsyncThunk(
  'companyList/minimal',
  async (payload, { getState }) => {
    const minimalCompanyList = getMinimalCompanyList(getState());
    if (minimalCompanyList.length > 0) {
      return minimalCompanyList;
    }

    const user = getUser(getState());
    const isMinearcRole = hasMinearcAuthority(user);
    const isAdmin = getIsAdminApi(getState());

    let url = 'v1/site/all/minimal';
    if (isAdmin || isMinearcRole) {
      url = 'v1/admin/tenant/minimal';
    }

    const primary = await GET(url);
    const primaryList = normalizeList(primary);

    if (isAdmin || isMinearcRole) {
      return primaryList;
    }

    if (hasReadableTenantName(primaryList)) {
      return primaryList;
    }

    // For site/tenant roles some environments return tenant IDs only.
    // Try deriving tenant labels from site payloads as a fallback.
    const fallbackEndpoints = ['v1/site', 'v1/site/all'];

    for (const endpoint of fallbackEndpoints) {
      const [err, response] = await to(GET(endpoint));
      if (err) continue;

      const sites = normalizeList(response);
      const tenantOptions = buildTenantOptionsFromSites(sites);
      if (tenantOptions.length > 0) {
        return tenantOptions;
      }
    }

    return primaryList;
  },
);

export const enableDisabledCompany = createAsyncThunk('company/enableDisable', async ({ value, id }) => {
  return POST(`v1/admin/tenant/${id}/${value ? 'enable' : 'disable'}`);
});

export const getCompanyDetailsById = createAsyncThunk('company/getById', async ({ id }) => {
  return GET(`v1/admin/tenant/${id}`);
});

export const addNewCompany = createAsyncThunk(
  'company/addNew',
  async ({ formValue, image }, { rejectWithValue }) => {
    try {
      let data = null;

      if (image) {
        const [err, res] = await to(POST_IMAGE(image));
        if (err) {
          return rejectWithValue(err.response?.data?.error || { message: 'Image upload failed' });
        }
        data = res;
      }

      const response = await POST('v1/admin/tenant', {
        ...formValue,
        imageUrl: data ? data.secure_url : null,
      });

      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || { message: 'Something went wrong' });
    }
  },
);

export const updateCompany = createAsyncThunk(
  'company/update',
  async ({ id, body, image }, { rejectWithValue }) => {
    try {
      let err, data;
      if (image) {
        [err, data] = await to(POST_IMAGE(image));
        if (err) {
          return rejectWithValue({ err });
        }
        body.imageUrl = data.secure_url;
      }

      return await PUT('v1/admin/tenant/' + id, body);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const CompanyAction = {
  setPage: (page) => (dispatch, getState) => {
    const tableInfo = getCompanyTableInfo(getState());
    if (tableInfo.currentPage !== page) {
      dispatch(getPaginatedCompanyList({ page, ...tableInfo, sortColumn: 'name' }));
    }
  },
  setSortColumn: (sortColumn, isAscending) => (dispatch, getState) => {
    const tableInfo = getCompanyTableInfo(getState());
    dispatch(getPaginatedCompanyList({ page: 0, ...tableInfo, sortColumn, isAscending }));
  },
  setSearch: (searchKey) => (dispatch, getState) => {
    const tableInfo = getCompanyTableInfo(getState());
    dispatch(
      getPaginatedCompanyList({
        page: 0,
        ...tableInfo,
        searchKey,
      }),
    );
  },
};

export const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setTableInfo: (state, { payload }) => {
      state.tableInfo = { ...state.tableInfo, ...payload };
    },
    setSelectedCompany: (state, { payload }) => {
      state.selectedCompany = payload;
    },
  },
  extraReducers: {
    [getPaginatedCompanyList.pending]: (state) => {
      state.isLoading = true;
      state.isLoadingError = false;
    },
    [getPaginatedCompanyList.rejected]: (state) => {
      state.isLoading = false;
      state.isLoadingError = true;
    },
    [getPaginatedCompanyList.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { results, currentPage, pageSize, totalPages } = action.payload;
      state.companyList = results;
      state.tableInfo = { ...state.tableInfo, currentPage, pageSize, totalPages };
    },
    [fetchMinimalCompanyList.pending]: (state) => {
      state.minimalCompanyListLoading = true;
    },
    [fetchMinimalCompanyList.rejected]: (state) => {
      state.minimalCompanyListLoading = false;
    },
    [fetchMinimalCompanyList.fulfilled]: (state, { payload }) => {
      state.minimalCompanyListLoading = false;
      state.minimalCompanyList = Array.isArray(payload) ? payload : [];
    },
    [getCompanyDetailsById.pending]: (state) => {
      state.isLoading = true;
    },
    [getCompanyDetailsById.rejected]: (state) => {
      state.isLoading = false;
    },
    [getCompanyDetailsById.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.selectedCompany = payload;
    },
    [enableDisabledCompany.pending]: (state) => {
      state.isLoading = true;
    },
    [enableDisabledCompany.rejected]: (state) => {
      state.isLoading = false;
    },
    [enableDisabledCompany.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.companyList = state.companyList.map((company) =>
        company.identifier === payload.identifier ? payload : company,
      );
    },
    [addNewCompany.pending]: (state) => {
      state.addNewCompanyLoading = true;
    },
    [addNewCompany.rejected]: (state) => {
      state.addNewCompanyLoading = false;
    },
    [addNewCompany.fulfilled]: (state) => {
      state.addNewCompanyLoading = false;
      state.companyList = [];
      state.minimalCompanyList = [];
      state.tableInfo.currentPage = -1;
    },
  },
});

export const { setTableInfo, setSelectedCompany, setSearch } = companySlice.actions;

const getCompanyStore = (state) => state.company;

export const getCompanyList = createSelector(getCompanyStore, (state) => state.companyList);
export const getCompanyLoading = createSelector(getCompanyStore, (state) => state.isLoading);
export const getAddNewCompanyLoading = createSelector(getCompanyStore, (state) => state.addNewCompanyLoading);
export const getCompanyTableInfo = createSelector(getCompanyStore, (state) => state.tableInfo);
export const getSelectedCompany = createSelector(getCompanyStore, (state) => state.selectedCompany);
export const getMinimalCompanyList = createSelector(getCompanyStore, (state) => state.minimalCompanyList);
export const getMinimalCompanyListLoading = createSelector(
  getCompanyStore,
  (state) => state.minimalCompanyListLoading,
);

export default companySlice.reducer;